import { EditorState } from '@codemirror/state';
import { EditorView, keymap, lineNumbers, highlightActiveLine, highlightTrailingWhitespace } from '@codemirror/view';
import { indentUnit, bracketMatching } from '@codemirror/language';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { autocompletion, closeBrackets, closeBracketsKeymap } from '@codemirror/autocomplete';
import { search, searchKeymap, highlightSelectionMatches } from '@codemirror/search';
import { python } from '@codemirror/lang-python';

export const DEFAULT_EDITOR_SETTINGS = {
  themeId: 'dark',
  fontSizePx: 14,
  lineHeight: 1.45,

  lineNumbers: true,
  lineWrapping: true,

  closeBrackets: true,
  bracketMatching: true,

  autocompleteMode: 'off', // off | manual | typing
  showHints: false,
  showSearch: true, // Ctrl+F / Ctrl+H / Ctrl+G

  highlightActiveLine: true,
  highlightTrailingWhitespace: false,
  highlightSelectionMatches: true,

  // Вертикальные линейки (колонки в символах, например [80, 100])
  rulersEnabled: false,
  rulersColumns: [80],

  // «Направляющие отступов» (упрощённо: вертикальные линии каждые 4ch)
  indentGuides: false,

  autosaveDrafts: true,
};

function clampNumber(value, { min, max, fallback }) {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

function normalizeSettings(raw) {
  const s = { ...DEFAULT_EDITOR_SETTINGS, ...(raw && typeof raw === 'object' ? raw : {}) };

  s.fontSizePx = Math.round(clampNumber(s.fontSizePx, { min: 10, max: 24, fallback: DEFAULT_EDITOR_SETTINGS.fontSizePx }));
  s.lineHeight = clampNumber(s.lineHeight, { min: 1.1, max: 2.2, fallback: DEFAULT_EDITOR_SETTINGS.lineHeight });
  s.rulersColumns = Array.isArray(s.rulersColumns) ? s.rulersColumns.filter((x) => Number.isFinite(Number(x))).map((x) => Math.round(Number(x))) : DEFAULT_EDITOR_SETTINGS.rulersColumns;
  if (s.rulersColumns.length === 0) s.rulersColumns = [...DEFAULT_EDITOR_SETTINGS.rulersColumns];

  if (!['off', 'manual', 'typing'].includes(s.autocompleteMode)) s.autocompleteMode = DEFAULT_EDITOR_SETTINGS.autocompleteMode;
  // Принудительно держим перенос длинных строк включённым, чтобы вставка длинного текста
  // сразу отображалась по ширине редактора и не уходила в горизонтальный скролл.
  s.lineWrapping = true;

  return s;
}

function tabToFourSpaces(view) {
  const spaces = '    ';
  const state = view.state;
  const doc = state.doc;
  const { from, to } = state.selection.main;

  // Индентируем все строки, пересекающие выделение.
  // Если выделение заканчивается ровно на EOL, берем `to - 1`,
  // чтобы не захватить лишнюю следующую строку.
  const effectiveTo = from === to ? to : Math.max(from, to - 1);
  const fromLine = doc.lineAt(from).number;
  const toLine = doc.lineAt(effectiveTo).number;

  const changes = [];
  for (let lineNo = fromLine; lineNo <= toLine; lineNo += 1) {
    const line = doc.line(lineNo);
    changes.push({ from: line.from, to: line.from, insert: spaces });
  }

  if (changes.length > 0) {
    if (from === to) {
      const cursorLine = doc.lineAt(from).number;
      const lineShift = (cursorLine - fromLine + 1) * spaces.length;
      const nextPos = from + lineShift;
      view.dispatch({
        changes,
        selection: { anchor: nextPos, head: nextPos },
      });
    } else {
      view.dispatch({ changes });
    }
  }
  return true;
}

function shiftTabToLessSpaces(view) {
  const spacesToRemove = 4;
  const state = view.state;
  const doc = state.doc;
  const { from, to } = state.selection.main;
  const effectiveTo = from === to ? to : Math.max(from, to - 1);

  const fromLine = doc.lineAt(from).number;
  const toLine = doc.lineAt(effectiveTo).number;

  const changes = [];
  for (let lineNo = fromLine; lineNo <= toLine; lineNo += 1) {
    const line = doc.line(lineNo);
    const maxCheck = Math.min(line.from + spacesToRemove, line.to);
    const prefix = doc.sliceString(line.from, maxCheck);
    const m = prefix.match(/^ +/);
    const removeCount = m ? Math.min(m[0].length, spacesToRemove) : 0;
    if (removeCount > 0) {
      changes.push({ from: line.from, to: line.from + removeCount, insert: '' });
    }
  }

  if (changes.length > 0) {
    view.dispatch({ changes });
  }
  return true;
}

function tabsToSpacesTransactionFilter() {
  return EditorState.transactionFilter.of((tr) => {
    if (!tr.docChanged) return tr;
    const changes = [];
    tr.changes.iterChanges((fromA, toA, fromB, toB, inserted) => {
      const text = inserted.toString();
      if (!text.includes('\t')) return;
      const replaced = text.replace(/\t/g, '    ');
      // В follow-up changes координаты должны соответствовать "новому" документу
      // транзакции (B), чтобы заменить вставленные табы, а не добавить поверх.
      // Чтобы это работало при пустом документе (startDocLength=0), возвращаем
      // follow-up с `sequential: true` ниже.
      changes.push({ from: fromB, to: toB, insert: replaced });
    });
    if (changes.length === 0) return tr;
    // sequential=true гарантирует, что follow-up changes интерпретируются
    // относительно docSize после применения основной транзакции.
    return [tr, { changes, sequential: true }];
  });
}

function buildRulersTheme(columns) {
  // Рисуем вертикальные линии на .cm-content, в единицах ch.
  const images = columns.map((col) => `linear-gradient(to right, transparent calc(${col}ch - 0.5px), rgba(255,255,255,0.08) calc(${col}ch - 0.5px), rgba(255,255,255,0.08) ${col}ch, transparent ${col}ch)`);
  return EditorView.theme({
    '.cm-content': {
      backgroundImage: images.join(','),
      backgroundRepeat: 'no-repeat',
    },
  });
}

function buildIndentGuidesTheme() {
  // Упрощённые “направляющие”: линия каждые 4ch.
  return EditorView.theme({
    '.cm-content': {
      backgroundImage: 'repeating-linear-gradient(to right, rgba(255,255,255,0.06), rgba(255,255,255,0.06) 1px, transparent 1px, transparent 4ch)',
      backgroundRepeat: 'repeat',
    },
  });
}

export function buildPythonEditorExtensions({
  settings: rawSettings,
  themeExt,
  height,
  readOnly,
  includeRunKeymap,
  runKeymap,
}) {
  const settings = normalizeSettings(rawSettings);

  const exts = [
    python(),
    themeExt,
    indentUnit.of('    '),
    EditorState.tabSize.of(4),

    ...(settings.lineNumbers ? [lineNumbers()] : []),
    ...(settings.highlightActiveLine ? [highlightActiveLine()] : []),
    history(),

    ...(settings.bracketMatching ? [bracketMatching()] : []),
    ...(settings.closeBrackets ? [closeBrackets(), keymap.of(closeBracketsKeymap)] : []),

    ...(settings.highlightTrailingWhitespace ? [highlightTrailingWhitespace()] : []),
    ...(settings.highlightSelectionMatches ? [highlightSelectionMatches()] : []),

    ...(settings.showSearch ? [search({})] : []),

    ...(settings.autocompleteMode === 'off'
      ? []
      : [autocompletion({ activateOnTyping: settings.autocompleteMode === 'typing' })]),

    keymap.of([
      ...(Array.isArray(defaultKeymap) ? defaultKeymap : []),
      ...(Array.isArray(historyKeymap) ? historyKeymap : []),
      ...(settings.showSearch && Array.isArray(searchKeymap) ? searchKeymap : []),
      { key: 'Tab', run: tabToFourSpaces },
      { key: 'Shift-Tab', run: shiftTabToLessSpaces },
    ]),

    tabsToSpacesTransactionFilter(),

    ...(settings.lineWrapping ? [EditorView.lineWrapping] : []),

    EditorView.theme({
      '&': {
        height: height ?? '100%',
        fontSize: `${settings.fontSizePx}px`,
      },
      '.cm-scroller': {
        overflow: 'auto',
        fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
        backgroundColor: 'inherit',
      },
      '.cm-content': {
        backgroundColor: 'inherit',
        lineHeight: String(settings.lineHeight),
      },
    }),

    ...(settings.rulersEnabled ? [buildRulersTheme(settings.rulersColumns)] : []),
    ...(settings.indentGuides ? [buildIndentGuidesTheme()] : []),
  ];

  if (includeRunKeymap && runKeymap) exts.push(runKeymap);
  if (readOnly) exts.push(EditorState.readOnly.of(true), EditorView.editable.of(false));

  return exts;
}

